import { forwardRef, useEffect, useRef, useState } from "react";
import { classNames } from "../utils";
import ReactTooltip from "react-tooltip";
import {
  DocumentDuplicateIcon,
  EyeIcon,
  InformationCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

interface IInputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  handleChange?: ({ target }: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur?: ({ target }: React.ChangeEvent<HTMLInputElement>) => void;
  handleEnter?: () => void;
  type?: string;
  name: string;
  hideText?: boolean;
  label?: string;
  classes?: string;
  labelClassName?: string;
  onClear?: () => void;
  tooltip?: string;
  tooltipClassName?: string;
  units?: string;
  isCopyable?: boolean;
}

const InputField = forwardRef<HTMLInputElement, IInputFieldProps>(
  (
    {
      handleChange,
      type,
      name,
      label,
      classes,
      handleBlur,
      handleEnter,
      labelClassName,
      required,
      value,
      tooltip,
      tooltipClassName,
      onClear,
      hideText = false,
      units,
      isCopyable = false,
      ...rest
    },
    ref
  ) => {
    const [isTextVisible, setTextVisible] = useState(!hideText);
    const isFirstRender = useRef(true); // Ref to track first render
    const handleKeyDown = ({ key }: React.KeyboardEvent<HTMLInputElement>) => {
      if (key === "Enter" && handleEnter) {
        handleEnter();
      }
    };

    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false; // Mark first render as done
      } else if (value !== undefined) {
        setTextVisible(true);
      }
    }, [value]);

    const handleVisibilityToggle = () => {
      setTextVisible(!isTextVisible);
    };

    const handleClear = () => {
      if (onClear) {
        onClear();
        value = "";
      }
    };
    return (
      <div className={classNames(classes ?? "w-96", "relative")}>
        {label && (
          <label
            htmlFor={name}
            className={classNames(
              "block text-sm font-medium text-gray-600 mb-2",
              labelClassName ?? ""
            )}
          >
            {label} {required && <span className="text-red-400">*</span>}
            {tooltip && (
              <>
                <span data-tip data-for={`${name}-tooltip`}>
                  <InformationCircleIcon
                    className={
                      tooltipClassName ?? "inline-flex w-6 text-gray-500"
                    }
                  />
                </span>
                <ReactTooltip
                  className="hover:visible:opacity-1 w-96"
                  delayHide={300}
                  effect="solid"
                  html={false}
                  place="right"
                  id={`${name}-tooltip`}
                >
                  {tooltip}
                </ReactTooltip>
              </>
            )}
          </label>
        )}
        <div className="relative">
          <input
            id={name}
            type={isTextVisible ? type : "password"}
            onChange={handleChange}
            autoComplete="off"
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            name={name}
            value={value} // Set the input value
            {...rest}
            className={classNames(
              rest.className ?? "w-full p-2.5",
              "border text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 ",
              units ? "inline " : "block ",
              rest.autoComplete
                ? "p-0 border-none focus-visible:outline-none"
                : "p-2.5",
              rest.readOnly ? "bg-gray-100" : ""
            )}
            ref={ref}
          />{" "}
          {units}
          {(hideText || isCopyable || onClear) && (
            <div className="absolute flex  rounded-r-lg  border inset-y-0 right-0">
              {hideText && (
                <button
                  type="button"
                  onClick={handleVisibilityToggle}
                  className={classNames(
                    "px-2 bg-gray-50 hover:text-ourGreen",
                    isTextVisible ? "text-ourGreen" : "text-gray-400",
                    isCopyable || onClear ? "border-r" : "rounded-r-lg"
                  )}
                >
                  <EyeIcon className="w-5 h-5" />
                </button>
              )}
              {isCopyable && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText((value ?? "").toString());
                  }}
                  className={classNames(
                    "px-2 bg-gray-50 text-gray-400",
                    onClear ? "border-r" : "rounded-r-lg"
                  )}
                >
                  <DocumentDuplicateIcon className="w-5 h-5" />
                </button>
              )}
              {onClear && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-2 bg-gray-50 rounded-r-lg hover:text-red-600 text-gray-400"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default InputField;
